import {
	BarChart3,
	CheckCircle2,
	MessageSquare,
	Percent,
	Trash2,
	Users,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCampaignAdmin,
	useCampaignResponses,
	useCampaignResults,
	useDeleteCampaignResponse,
	useExportCampaignResponses,
} from '@/api/queries/campaigns-queries'
import { BarListRow } from '@/components/administration/kpi-metrics/shared/bar-list-row'
import { KpiStatCard } from '@/components/administration/kpi-metrics/shared/kpi-stat-card'
import {
	AdminCardPage,
	ConfirmDialog,
	type ExportFormat,
	ExportMenu,
} from '@/components/commons'
import { Button } from '@/components/ui/button'
import { NativeSelect } from '@/components/ui/native-select'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/lib/utils'
import type {
	CampaignAnswerOutput,
	CampaignQuestionResult,
	CampaignResponseOutput,
	CampaignResultsParams,
} from '@/types/campaign'
import { getApiErrorMessage } from '@/utils/api-error'
import { formatLocalDate } from '@/utils/campaign-dates'
import { downloadBlob } from '@/utils/download-blob'

const formatPct = (value: number) => `${value}%`

function answerLabel(
	answer: CampaignAnswerOutput,
	t: (key: string) => string,
): string {
	if (answer.booleanValue !== null) {
		return t(
			answer.booleanValue ? 'campaigns.respond.yes' : 'campaigns.respond.no',
		)
	}
	if (answer.numberValue !== null) return String(answer.numberValue)
	if (answer.choiceValues.length > 0) return answer.choiceValues.join('; ')
	return answer.textValue ?? answer.fileName ?? '-'
}

function QuestionResultCard({ result }: { result: CampaignQuestionResult }) {
	const { t } = useTranslation()
	const maxScale = Math.max(
		1,
		...(result.scale?.distribution.map((d) => d.count) ?? [0]),
	)

	return (
		<div className="flex flex-col gap-2 rounded-lg border border-kaefer-light-silver bg-white p-4">
			<div className="flex items-start justify-between gap-2">
				<span className="text-sm font-semibold text-kaefer-dark-grey">
					{result.label}
				</span>
				<span className="shrink-0 text-xs text-muted-foreground">
					{t('campaigns.results.answeredCount', { count: result.answered })}
				</span>
			</div>

			{result.choices?.map((choice) => (
				<BarListRow
					key={choice.value}
					label={choice.value}
					displayValue={`${choice.count} (${formatPct(choice.pct)})`}
					widthPct={choice.pct}
					colorClass="bg-kaefer-sapphire-blue"
				/>
			))}

			{result.boolean && (
				<>
					<BarListRow
						label={t('campaigns.respond.yes')}
						displayValue={String(result.boolean.yes)}
						widthPct={
							result.answered ? (result.boolean.yes / result.answered) * 100 : 0
						}
						colorClass="bg-kaefer-forest-green"
					/>
					<BarListRow
						label={t('campaigns.respond.no')}
						displayValue={String(result.boolean.no)}
						widthPct={
							result.answered ? (result.boolean.no / result.answered) * 100 : 0
						}
						colorClass="bg-kaefer-red"
					/>
				</>
			)}

			{result.scale && (
				<>
					<p className="text-sm text-kaefer-dark-grey">
						{t('campaigns.results.average')}:{' '}
						<strong>{result.scale.average}</strong>
					</p>
					{result.scale.distribution.map((item) => (
						<BarListRow
							key={item.value}
							label={String(item.value)}
							displayValue={String(item.count)}
							widthPct={(item.count / maxScale) * 100}
							colorClass="bg-kaefer-violet"
						/>
					))}
				</>
			)}

			{result.texts && (
				<ul className="flex max-h-56 flex-col gap-2 overflow-y-auto pr-1">
					{result.texts.map((item, index) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: amostra de respostas sem id
						<li
							key={index}
							className="rounded-md bg-kaefer-light-silver/30 p-2 text-sm"
						>
							<p className="whitespace-pre-wrap break-words text-kaefer-dark-grey">
								{item.text}
							</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{item.personName} · {formatLocalDate(item.date)}
							</p>
						</li>
					))}
					{result.answered > result.texts.length && (
						<li className="text-xs text-muted-foreground">
							{t('campaigns.results.textsHint')}
						</li>
					)}
				</ul>
			)}

			{result.files !== undefined && (
				<p className="text-sm text-muted-foreground">
					{t('campaigns.results.filesCount', { count: result.files })}
				</p>
			)}
		</div>
	)
}

/** Resultados e KPIs de uma campanha: participação, resumo por pergunta, quem respondeu e as respostas. */
export const CampaignResultsPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id = '' } = useParams<{ id: string }>()

	const [status, setStatus] = useState<
		CampaignResultsParams['personStatus'] | ''
	>('')
	const [jobRole, setJobRole] = useState('')
	const [branchId, setBranchId] = useState('')
	const [responsesPage, setResponsesPage] = useState(1)

	const { data: campaign } = useCampaignAdmin(id)
	const { data, isLoading, isError } = useCampaignResults(id, {
		...(status ? { personStatus: status } : {}),
		...(jobRole ? { personJobRole: jobRole } : {}),
		...(branchId ? { personBranchId: branchId } : {}),
	})
	const { data: responses } = useCampaignResponses(id, responsesPage)
	const exportMutation = useExportCampaignResponses()
	const deleteResponseMutation = useDeleteCampaignResponse(id)
	const [deletingResponse, setDeletingResponse] =
		useState<CampaignResponseOutput | null>(null)

	const confirmDeleteResponse = async () => {
		if (!deletingResponse) return
		try {
			await deleteResponseMutation.mutateAsync(deletingResponse.id)
			toast.success(t('campaigns.results.responseDeleted'))
			// apagou o único item da última página: volta uma página
			if (responsesPage > 1 && responses?.items.length === 1) {
				setResponsesPage((page) => page - 1)
			}
			setDeletingResponse(null)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleExport = (format: ExportFormat) => {
		if (format === 'pdf') return
		exportMutation.mutate(
			{ id, format },
			{
				onSuccess: (blob) =>
					downloadBlob(blob, `questionario-respostas.${format}`),
				onError: () => toast.error(t('common.exportError')),
			},
		)
	}

	const maxTimeline = Math.max(
		1,
		...(data?.timeline.map((day) => day.responses) ?? [0]),
	)

	return (
		<AdminCardPage
			icon={<BarChart3 className="size-5" />}
			title={campaign?.title ?? t('campaigns.results.title')}
			subtitle={t('campaigns.results.subtitle')}
			onBack={() => navigate(ROUTES.ADMINISTRATION.CAMPAIGNS)}
			actions={
				<ExportMenu
					formats={['xlsx', 'csv']}
					onExport={handleExport}
					isExporting={exportMutation.isPending}
				/>
			}
		>
			{isLoading && !data ? (
				<p className="py-16 text-center text-muted-foreground text-sm">
					{t('common.loading')}
				</p>
			) : isError || !data ? (
				<p className="py-16 text-center text-destructive text-sm">
					{t('campaigns.results.loadError')}
				</p>
			) : (
				<div className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
						<KpiStatCard
							icon={<Percent className="size-4" />}
							iconClass="bg-kaefer-violet/15 text-kaefer-violet"
							label={t('campaigns.results.participation')}
							value={formatPct(data.totals.participationPct)}
						/>
						<KpiStatCard
							icon={<Users className="size-4" />}
							iconClass="bg-kaefer-dark-grey/10 text-kaefer-dark-grey"
							label={t('campaigns.results.audience')}
							value={String(data.totals.peopleInScope)}
						/>
						<KpiStatCard
							icon={<CheckCircle2 className="size-4" />}
							iconClass="bg-kaefer-forest-green/15 text-kaefer-forest-green"
							label={t('campaigns.results.respondents')}
							value={String(data.totals.respondents)}
						/>
						<KpiStatCard
							icon={<MessageSquare className="size-4" />}
							iconClass="bg-kaefer-sapphire-blue/15 text-kaefer-sapphire-blue"
							label={t('campaigns.results.responses')}
							value={String(data.totals.responses)}
						/>
					</div>

					<div className="grid gap-4 lg:grid-cols-3">
						<SummaryCard
							title={t('campaigns.results.timeline')}
							empty={t('campaigns.results.empty')}
						>
							{data.timeline.map((day) => (
								<BarListRow
									key={day.date}
									label={formatLocalDate(`${day.date}T12:00:00`)}
									displayValue={String(day.responses)}
									widthPct={(day.responses / maxTimeline) * 100}
									colorClass="bg-kaefer-violet"
								/>
							))}
						</SummaryCard>
						<SummaryCard
							title={t('campaigns.results.byBranch')}
							empty={t('campaigns.results.empty')}
						>
							{data.byBranch.map((branch) => (
								<BarListRow
									key={branch.branchId}
									label={branch.branchName}
									displayValue={`${formatPct(branch.participationPct)} (${branch.respondents}/${branch.people})`}
									widthPct={branch.participationPct}
									colorClass="bg-kaefer-forest-green"
									onClick={() => setBranchId(branch.branchId)}
								/>
							))}
						</SummaryCard>
						<SummaryCard
							title={t('campaigns.results.byJobRole')}
							empty={t('campaigns.results.empty')}
						>
							{data.byJobRole.map((role) => (
								<BarListRow
									key={role.jobRole}
									label={role.jobRole}
									displayValue={`${formatPct(role.participationPct)} (${role.respondents}/${role.people})`}
									widthPct={role.participationPct}
									colorClass="bg-kaefer-sapphire-blue"
									onClick={() => setJobRole(role.jobRole)}
								/>
							))}
						</SummaryCard>
					</div>

					<h2 className="text-sm font-semibold text-kaefer-dark-grey">
						{t('campaigns.results.questions')}
					</h2>
					<div className="grid gap-3 lg:grid-cols-2">
						{data.questions.map((question) => (
							<QuestionResultCard key={question.questionId} result={question} />
						))}
					</div>

					<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
						<div className="mb-3 flex flex-col gap-3">
							<span className="text-sm font-semibold text-kaefer-dark-grey">
								{t('campaigns.results.people')}
							</span>
							<div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
								<SearchableSelect
									options={data.jobRoles.map((role) => ({
										value: role,
										label: role,
									}))}
									value={jobRole}
									onValueChange={setJobRole}
									placeholder={t('campaigns.results.allRoles')}
									searchPlaceholder={t('campaigns.results.searchRole')}
									emptyMessage={t('campaigns.results.empty')}
								/>
								<SearchableSelect
									options={data.branches.map((branch) => ({
										value: branch.id,
										label: branch.name,
									}))}
									value={branchId}
									onValueChange={setBranchId}
									placeholder={t('campaigns.results.allBranches')}
									searchPlaceholder={t('campaigns.results.searchBranch')}
									emptyMessage={t('campaigns.results.empty')}
								/>
								<NativeSelect
									aria-label={t('campaigns.results.status')}
									value={status}
									onChange={(e) =>
										setStatus(
											e.target.value as
												| CampaignResultsParams['personStatus']
												| '',
										)
									}
								>
									<option value="">{t('campaigns.results.all')}</option>
									<option value="responded">
										{t('campaigns.results.responded')}
									</option>
									<option value="notResponded">
										{t('campaigns.results.notResponded')}
									</option>
								</NativeSelect>
							</div>
						</div>
						<p className="mb-2 text-xs text-muted-foreground">
							{t('campaigns.results.summary', {
								responded: data.peopleSummary.responded,
								total: data.peopleSummary.total,
							})}
						</p>
						<div className="max-h-96 overflow-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('campaigns.results.name')}</TableHead>
										<TableHead>{t('campaigns.results.badge')}</TableHead>
										<TableHead>{t('campaigns.results.jobRole')}</TableHead>
										<TableHead>{t('campaigns.results.branch')}</TableHead>
										<TableHead>{t('campaigns.results.cr')}</TableHead>
										<TableHead className="text-right">
											{t('campaigns.results.responsesShort')}
										</TableHead>
										<TableHead>{t('campaigns.results.lastResponse')}</TableHead>
										<TableHead>{t('campaigns.results.status')}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{data.people.map((person) => (
										<TableRow key={person.personId}>
											<TableCell className="font-medium">
												{person.name}
											</TableCell>
											<TableCell>{person.badge}</TableCell>
											<TableCell>{person.jobRole ?? '-'}</TableCell>
											<TableCell>{person.branchName}</TableCell>
											<TableCell>{person.crName}</TableCell>
											<TableCell className="text-right tabular-nums">
												{person.responses}
											</TableCell>
											<TableCell>
												{person.lastResponseAt
													? formatLocalDate(`${person.lastResponseAt}T12:00:00`)
													: '-'}
											</TableCell>
											<TableCell>
												<span
													className={cn(
														'rounded-full px-2 py-0.5 text-xs font-semibold',
														person.responded
															? 'bg-kaefer-forest-green/15 text-kaefer-forest-green'
															: 'bg-kaefer-medium-grey/20 text-kaefer-dark-grey',
													)}
												>
													{t(
														person.responded
															? 'campaigns.results.responded'
															: 'campaigns.results.notResponded',
													)}
												</span>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
						{data.peopleTruncated && (
							<p className="mt-2 text-xs text-muted-foreground">
								{t('campaigns.results.truncated')}
							</p>
						)}
					</div>

					<div className="flex flex-col gap-3 rounded-lg border border-kaefer-light-silver bg-white p-4">
						<span className="text-sm font-semibold text-kaefer-dark-grey">
							{t('campaigns.results.individual')}
						</span>
						{responses?.items.length ? (
							<>
								{responses.items.map((response) => (
									<div
										key={response.id}
										className="rounded-md border border-kaefer-light-silver/70 p-3"
									>
										<div className="flex items-start justify-between gap-2">
											<p className="text-sm font-semibold text-kaefer-dark-grey">
												{response.personName}
												<span className="ml-2 text-xs font-normal text-muted-foreground">
													{response.badge} · {response.branchName} ·{' '}
													{new Date(response.submittedAt).toLocaleString(
														'pt-BR',
													)}
												</span>
											</p>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="size-8 w-auto shrink-0 px-2 text-destructive"
												title={t('campaigns.results.deleteResponse')}
												onClick={() => setDeletingResponse(response)}
											>
												<Trash2 className="size-4" />
											</Button>
										</div>
										<dl className="mt-2 flex flex-col gap-1.5">
											{response.answers.map((answer) => (
												<div key={answer.questionId} className="text-sm">
													<dt className="text-xs text-muted-foreground">
														{answer.question}
													</dt>
													<dd className="whitespace-pre-wrap break-words text-kaefer-dark-grey">
														{answer.fileUrl ? (
															<a
																href={answer.fileUrl}
																target="_blank"
																rel="noreferrer"
																className="text-kaefer-sapphire-blue underline"
															>
																{answer.fileName}
															</a>
														) : (
															answerLabel(answer, t)
														)}
													</dd>
												</div>
											))}
										</dl>
									</div>
								))}
								<div className="flex items-center justify-between">
									<Button
										type="button"
										variant="outline"
										className="w-auto"
										disabled={responsesPage <= 1}
										onClick={() => setResponsesPage((page) => page - 1)}
									>
										{t('campaigns.results.previous')}
									</Button>
									<span className="text-xs text-muted-foreground">
										{responses.pagination.page}/
										{Math.max(1, responses.pagination.totalPages)}
									</span>
									<Button
										type="button"
										variant="outline"
										className="w-auto"
										disabled={responsesPage >= responses.pagination.totalPages}
										onClick={() => setResponsesPage((page) => page + 1)}
									>
										{t('campaigns.results.next')}
									</Button>
								</div>
							</>
						) : (
							<p className="text-xs text-muted-foreground">
								{t('campaigns.results.empty')}
							</p>
						)}
					</div>
				</div>
			)}

			<ConfirmDialog
				open={!!deletingResponse}
				onOpenChange={(open) => !open && setDeletingResponse(null)}
				title={t('campaigns.results.deleteResponseTitle')}
				onConfirm={confirmDeleteResponse}
				isPending={deleteResponseMutation.isPending}
				confirmLabel={t('campaigns.results.deleteResponse')}
				cancelLabel={t('common.cancel')}
				destructive
			>
				{t('campaigns.results.deleteResponseMessage', {
					name: deletingResponse?.personName,
				})}
			</ConfirmDialog>
		</AdminCardPage>
	)
}

function SummaryCard({
	title,
	empty,
	children,
}: {
	title: string
	empty: string
	children: React.ReactNode[]
}) {
	return (
		<div className="rounded-lg border border-kaefer-light-silver bg-white p-4">
			<span className="text-sm font-semibold text-kaefer-dark-grey">
				{title}
			</span>
			<div className="mt-2 flex max-h-72 flex-col gap-1.5 overflow-y-auto pr-1">
				{children.length ? (
					children
				) : (
					<p className="text-xs text-muted-foreground">{empty}</p>
				)}
			</div>
		</div>
	)
}
